import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader, Badge, Button } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { CreateDialog } from '@/components/CreateDialog';
import { LeadCard } from '@/components/leads/LeadCard';
import { QuickUpdateSheet } from '@/components/leads/QuickUpdateSheet';
import { LeadEditSheet } from '@/components/leads/LeadEditSheet';
import { createLead } from '@/data/sources';
import { exportLeadsCsv, fetchLeadsPage } from '@/data/leads';
import { leadCardModel, todayIso, STATUS_OPTIONS, PRIORITY_OPTIONS, platformFilterGroups, platformFilterToQuery, callActionAllowed, facetOptions, categoryFilterOptions, type LeadRecord, type LeadsQuery } from '@/data/leads-model';
import { useLeadCategories } from '@/hooks/useLeadCategories';
import { CategoryChips } from '@/components/leads/CategoryChips';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { useIsDesktopPointer, useIsMobile } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';

/* /sales/leads — ALL leads of the selected domain, paged (GET /outreach/leads).
 * Mobile (< lg): readable cards with Website / Call / Email / Maps actions and in-field
 * quick update. Desktop: a dense table (+ owner Edit). Filters: search, status, route
 * day, priority, approach, PLATFORM BY NAME (grouped Platform / directory · Booking
 * software · Other — names + counts from the server facets, grouping from the central
 * config), CATEGORY right beside Platform (item 7; owner 05-10-2026 ~12:06 UTC: no Approach filter in the top bar — the workspace category list; counts from the disjunctive
 * server facet; chips on every row/card). Domain isolation is server-side: the query carries the active project_id and
 * the server returns only that project's leads. Owner "Export CSV" = exactly the current
 * filtered view, produced and scoped server-side. */

const PAGE = 50;
type Filters = Omit<LeadsQuery, 'projectId' | 'cursor' | 'limit' | 'platform' | 'platform_group' | 'platform_kind'> & { platformSel?: string };

export function LeadsPage() {
  const { activeWorkspace } = useAuth();
  const { active } = useActiveDomain();
  const isMobile = useIsMobile();
  const showCall = callActionAllowed(useIsDesktopPointer()); // owner: no Call action on desktop for now (LEAD_ACTIONS.callOnDesktop)
  const queryClient = useQueryClient();
  const canCreate = activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'operator';
  const canImport = activeWorkspace?.role === 'owner';
  const isOwner = activeWorkspace?.role === 'owner';
  const { list: categoryList } = useLeadCategories();

  const [filters, setFilters] = useState<Filters>({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [editing, setEditing] = useState<LeadRecord | null>(null);
  const [editingFull, setEditingFull] = useState<LeadRecord | null>(null);
  const today = todayIso();

  const projectId = active?.projectId;
  const { platformSel, ...plain } = filters;
  const serverFilters = useMemo(() => ({ ...plain, ...platformFilterToQuery(platformSel) }), [plain, platformSel]);
  const queryKey = useMemo(() => ['leads-page', projectId ?? 'all', serverFilters] as const, [projectId, serverFilters]);
  const query = useInfiniteQuery({
    queryKey,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => fetchLeadsPage({ projectId, cursor: pageParam, limit: PAGE, ...serverFilters }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
  const pages = query.data?.pages ?? [];
  const leads = pages.flatMap((p) => p.items);
  const total = pages[0]?.total ?? 0;
  const facets = pages[0]?.facets;
  const platformGroups = useMemo(() => platformFilterGroups(facets), [facets]);

  const patchLocal = (updated: LeadRecord) => {
    queryClient.setQueryData(query.data ? queryKey : queryKey, (old: typeof query.data) =>
      old ? { ...old, pages: old.pages.map((p) => ({ ...p, items: p.items.map((l) => (l.lead_ref === updated.lead_ref ? updated : l)) })) } : old,
    );
    queryClient.invalidateQueries({ queryKey: ['lead-full', updated.lead_ref] });
  };

  const handleCreate = async (values: Record<string, string>) => {
    const { lead_ref } = await createLead({
      business_name: values.business_name, business_email: values.business_email, website: values.website,
      city_region: values.city_region, modality: values.modality, practitioner_name: values.practitioner_name,
    });
    setDialogOpen(false);
    setNotice(lead_ref ? `Lead added — reference ${lead_ref}.` : 'Lead added.');
    await queryClient.invalidateQueries({ queryKey: ['leads-page'] });
  };

  const handleExport = async () => {
    if (!projectId) { setNotice('Select a domain first — exports are scoped to one project.'); return; }
    setExporting(true); setNotice(null);
    try {
      const r = await exportLeadsCsv({ projectId, ...serverFilters });
      setNotice(`Exported ${r.rows ?? total} lead${(r.rows ?? total) === 1 ? '' : 's'} to ${r.filename} (the current filtered view).`);
    } catch (e) {
      setNotice(e instanceof ApiError ? `Export failed: ${e.message}` : 'Export failed — check your connection and try again.');
    } finally {
      setExporting(false);
    }
  };

  const set = (k: keyof Filters, v: string) => setFilters((f) => ({ ...f, [k]: v || undefined }));
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  return (
    <div>
      <PageHeader
        title="Leads"
        subtitle={active ? `Prospects for ${active.hostname} — ${total} in view` : `All domains — ${total} in view`}
        actions={
          <div className="flex flex-wrap gap-2">
            {isOwner ? (
              <button
                type="button"
                onClick={() => void handleExport()}
                disabled={exporting || !projectId || total === 0}
                title={!projectId ? 'Select a domain first — exports are scoped to one project' : `Export the ${total} leads in view as CSV`}
                className="inline-flex h-control items-center rounded-md border border-border-default bg-surface px-3 text-sm font-medium text-text-primary hover:bg-hover disabled:opacity-50"
                data-testid="export-csv"
              >
                {exporting ? 'Exporting…' : 'Export CSV'}
              </button>
            ) : null}
            {isOwner ? (
              <Link to="/settings?tab=Lead%20categories" className="inline-flex h-control items-center rounded-md border border-border-default bg-surface px-3 text-sm font-medium text-text-primary hover:bg-hover" data-testid="manage-categories">
                Categories
              </Link>
            ) : null}
            {canImport ? (
              <Link to="/sales/import" className="inline-flex h-control items-center rounded-md border border-border-default bg-surface px-3 text-sm font-medium text-text-primary hover:bg-hover">
                Import spreadsheet
              </Link>
            ) : null}
            {canCreate ? <Button variant="primary" onClick={() => setDialogOpen(true)}>＋ Add lead</Button> : null}
          </div>
        }
      />

      {notice && <div className="mb-4 rounded-md border border-success/20 bg-success/10 px-3 py-2 text-sm text-success">{notice}</div>}

      {/* Filters — compact on mobile */}
      <div className="mb-3 grid grid-cols-2 gap-2 lg:grid-cols-7" data-testid="lead-filters">
        <input
          type="search"
          inputMode="search"
          placeholder="Search name, area, postcode, platform"
          className="col-span-2 h-11 rounded-md border border-border-default bg-surface px-3 text-sm lg:h-control"
          value={filters.q ?? ''}
          onChange={(e) => set('q', e.target.value)}
        />
        <Select value={filters.status ?? ''} onChange={(v) => set('status', v)} label="Status" options={STATUS_OPTIONS.map((o) => [o.value, `${o.label}${facets?.status?.[o.value] ? ` (${facets.status[o.value]})` : ''}`])} />
        <Select value={filters.route_day ?? ''} onChange={(v) => set('route_day', v)} label="Day" options={facetOptions(facets?.route_day, filters.route_day)} />
        <Select value={filters.priority ?? ''} onChange={(v) => set('priority', v)} label="Priority" options={PRIORITY_OPTIONS.map((o) => [o.value, `${o.label}${facets?.priority?.[o.value] ? ` (${facets.priority[o.value]})` : ''}`])} />
        <select
          aria-label="Platform"
          data-testid="platform-filter"
          className="h-11 rounded-md border border-border-default bg-surface px-2 text-sm text-text-primary lg:h-control"
          value={filters.platformSel ?? ''}
          onChange={(e) => set('platformSel', e.target.value)}
        >
          <option value="">Platform: all</option>
          {platformGroups.map((g) => (
            <optgroup key={g.key} label={g.title}>
              {g.options.map((o) => <option key={o.value} value={o.value}>{o.label}{o.count !== undefined ? ` (${o.count})` : ''}</option>)}
            </optgroup>
          ))}
        </select>
        <Select value={filters.category ?? ''} onChange={(v) => set('category', v)} label="Category" options={categoryFilterOptions(categoryList, facets?.category, filters.category)} testId="category-filter" />
        {/* Approach filter removed from the top bar (owner 05-10-2026 ~12:06 UTC: Category beside Platform instead;
            Approach stays on the card / detail / edit sheet / table column / CSV). */}
        {activeFilterCount > 0 ? (
          <button type="button" className="col-span-2 h-9 text-xs text-text-secondary underline lg:col-span-1" onClick={() => setFilters({})}>
            Clear {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''}
          </button>
        ) : null}
      </div>

      {query.isPending ? (
        <LoadingState rows={5} />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : leads.length === 0 ? (
        <EmptyState
          title={active ? `No leads yet for ${active.hostname}` : 'No leads yet'}
          teach={
            activeFilterCount > 0
              ? 'No leads match these filters. Clear them to see everything in this domain.'
              : active
                ? `Leads attached to ${active.hostname} appear here. Import the prospects workbook or add a lead — leads from other domains are never shown here.`
                : 'Leads appear here as they are imported, captured or discovered.'
          }
          action={canImport ? <Link to="/sales/import" className="text-sm font-medium text-engine underline">Import a spreadsheet</Link> : undefined}
        />
      ) : isMobile ? (
        <div className="space-y-2" data-testid="lead-cards">
          {leads.map((l) => <LeadCard key={l.lead_ref} lead={l} today={today} onQuickUpdate={setEditing} />)}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] border-collapse">
              <thead>
                <tr className="border-b border-border-subtle bg-inset/50 text-left text-[11px] uppercase tracking-wide text-text-tertiary">
                  <th className="px-3 py-2 font-semibold">Business</th>
                  <th className="px-3 py-2 font-semibold">Area</th>
                  <th className="px-3 py-2 font-semibold">Booking platform</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                  <th className="px-3 py-2 font-semibold">Approach</th>
                  <th className="px-3 py-2 font-semibold">Follow-up</th>
                  <th className="px-3 py-2 font-semibold">Day</th>
                  <th className="px-3 py-2 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => {
                  const m = leadCardModel(l, today);
                  return (
                    <tr key={l.lead_ref} className="group border-b border-border-subtle last:border-0 hover:bg-hover">
                      <td className="px-3 py-2.5">
                        <Link to={`/sales/leads/${encodeURIComponent(l.lead_ref)}`} className="block">
                          <div className="text-sm font-medium text-text-primary group-hover:text-engine">{m.title}{m.priority ? <span className="ml-1 text-[10px] text-text-tertiary">P{m.priority}</span> : null}</div>
                          <div className="text-xs text-text-tertiary">{[m.category, m.postcode].filter(Boolean).join(' · ')}</div>
                        </Link>
                        <CategoryChips names={m.categories} className="mt-1" />
                      </td>
                      <td className="px-3 py-2.5 text-sm text-text-secondary">{m.area ?? '—'}</td>
                      <td className="px-3 py-2.5 text-sm text-text-secondary"><span className="text-text-primary">{m.platform}</span><div className="text-[11px] text-text-tertiary">{m.platformKindLabel}</div></td>
                      <td className="px-3 py-2.5"><Badge tone={l.status === 'DISCOVERED' ? 'neutral' : 'compliance'}>{m.statusLabel}</Badge></td>
                      <td className="px-3 py-2.5 text-sm text-text-secondary">{m.approachLabel ?? '—'}</td>
                      <td className={cn('px-3 py-2.5 text-sm', m.followUpOverdue ? 'font-semibold text-danger' : 'text-text-secondary')}>{m.followUp ?? '—'}</td>
                      <td className="px-3 py-2.5 text-xs text-text-tertiary">{m.routeDay ?? '—'}</td>
                      <td className="px-3 py-2.5 text-right text-xs">
                        <div className="flex justify-end gap-2">
                          {m.actions.website ? <a className="text-engine underline" href={m.actions.website} target="_blank" rel="noopener noreferrer">Site</a> : null}
                          {m.actions.tel && showCall ? <a className="text-engine underline" href={m.actions.tel}>Call</a> : null}
                          {m.actions.mail ? <a className="text-engine underline" href={m.actions.mail}>Email</a> : null}
                          {m.actions.maps ? <a className="text-engine underline" href={m.actions.maps} target="_blank" rel="noopener noreferrer">Maps</a> : null}
                          <button type="button" className="text-text-secondary underline" onClick={() => setEditing(l)}>Update</button>
                          {isOwner ? <button type="button" className="text-text-secondary underline" onClick={() => setEditingFull(l)}>Edit</button> : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Footer keeps pb-20 below lg so the last card + Load more scroll clear of the fixed Aura orb (48 px at bottom/right 1.25rem). */}
      {leads.length > 0 ? (
        <div className="mt-3 flex items-center justify-between pb-20 text-xs text-text-tertiary lg:pb-0">
          <span>Showing {leads.length} of {total}</span>
          {query.hasNextPage ? (
            <Button variant="secondary" className="h-10" onClick={() => void query.fetchNextPage()} disabled={query.isFetchingNextPage}>
              {query.isFetchingNextPage ? 'Loading…' : `Load ${Math.min(PAGE, total - leads.length)} more`}
            </Button>
          ) : (
            <span>All {total} loaded</span>
          )}
        </div>
      ) : null}

      <QuickUpdateSheet lead={editing} open={editing !== null} onClose={() => setEditing(null)} onSaved={patchLocal} />
      <LeadEditSheet lead={editingFull} open={editingFull !== null} onClose={() => setEditingFull(null)} onSaved={patchLocal} projectId={projectId} />

      {canCreate && (
        <CreateDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          title="Add lead"
          description="Add a business to your pipeline. It arrives ready to enrich and score."
          submitLabel="Add lead"
          onSubmit={handleCreate}
          fields={[
            { name: 'business_name', label: 'Business name', placeholder: 'Studio name' },
            { name: 'business_email', label: 'Email', type: 'email', placeholder: 'hello@studio.com', hint: 'If provided, must be a valid email address.' },
            { name: 'website', label: 'Website', type: 'url', placeholder: 'https://studio.com' },
            { name: 'city_region', label: 'City / region', placeholder: 'Cardiff' },
            { name: 'modality', label: 'Modality', placeholder: 'Pilates' },
            { name: 'practitioner_name', label: 'Practitioner name', placeholder: 'Full name' },
          ]}
        />
      )}
    </div>
  );
}

function Select({ value, onChange, label, options, className, testId }: { value: string; onChange: (v: string) => void; label: string; options: Array<[string, string]>; className?: string; testId?: string }) {
  return (
    <select aria-label={label} data-testid={testId} className={cn('h-11 min-w-0 rounded-md border border-border-default bg-surface px-2 text-sm text-text-primary lg:h-control', className)} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{label}: all</option>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}
